import { isEditableTarget, matchShortcut } from '@tomo/ui';
import { useEffect, useRef } from 'react';

export interface HotkeyOptions {
  /** Fire even when typing in inputs/editors (default false, except for mod+ combos). */
  allowInEditable?: boolean;
  enabled?: boolean;
  preventDefault?: boolean;
}

/**
 * Bind one or more `'mod+k'`-style shortcuts on window. Single keys belong to the page, so they stay
 * quiet while focus is in a dialog on top of it.
 */
export function useHotkey(shortcuts: string | string[], handler: (e: KeyboardEvent) => void, options: HotkeyOptions = {}) {
  const ref = useRef(handler);
  ref.current = handler;
  const list = Array.isArray(shortcuts) ? shortcuts : [shortcuts];
  const key = list.join('|');
  const { enabled = true, preventDefault = true } = options;
  const allowInEditable = options.allowInEditable;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const hit = key.split('|').find((s) => matchShortcut(e, s));
      if (!hit) return;
      const hasMod = hit.includes('mod');
      if (isEditableTarget(e.target) && !(allowInEditable ?? hasMod)) return;
      if (!hit.includes('+') && e.target instanceof Element && e.target.closest('[role="dialog"]')) return;
      if (preventDefault) e.preventDefault();
      ref.current(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [key, enabled, preventDefault, allowInEditable]);
}
